/*
 * Dashboard constants, the only place they are defined: imported by the front end (@common/dashboard/constants)
 * and by the functions (common_dist/dashboard/constants)
 */

// Days and hours
const DAYS_IN_WEEK = 7;
const DAYS_IN_MONTH = 30;
const DAYS_IN_QUARTER = 90;
const HOURS_IN_DAY = 24;

// Full scale of a percentage
const PERCENT_MAX = 100;
// Shortest period that has a previous day to compare with
const PER_DAY_MIN_DAYS = 2;
// Days of the sparklines and trends of a one-day period
const TREND_DAYS = 14;

// Weekday keys, Monday first
const WEEKDAYS = Object.freeze(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);

// Date formats
const DATE_FORMAT = 'YYYY-MM-DD';
const MONTH_FORMAT = 'YYYY-MM';

// Chart bucket granularity
const GRAN = Object.freeze({
  HOUR: 'hour',
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
});

// Longest period (in days) drawn with each granularity
const DAY_GRANULARITY_MAX = 62;
const WEEK_GRANULARITY_MAX = 200;

// Period filter presets
const PRESET = Object.freeze({
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7: 'last7',
  LAST_30: 'last30',
  LAST_90: 'last90',
  THIS_MONTH: 'thisMonth',
  THIS_YEAR: 'thisYear',
  CUSTOM: 'custom',
});

// Comparison keys, suffix of dashboard.period.comparison.*
const COMPARISON = Object.freeze({
  PREVIOUS_DAY: 'previousDay',
  PREVIOUS_MONTH: 'previousMonth',
  PREVIOUS_YEAR: 'previousYear',
  PREVIOUS_PERIOD: 'previousPeriod',
});

// Age range keys stored in the counters
const AGE = Object.freeze({
  UNDER_18: '0017',
  FROM_18_TO_30: '1829',
  FROM_30_TO_50: '3049',
  OVER_50: '5000',
  UNKNOWN: '0000',
});

const PLATFORM = Object.freeze({
  GOOGLE: 'google',
  APPLE: 'apple',
});

// Gender values a profile can inform: male, female and other ('N' is not informed)
const INFORMED_GENDERS = Object.freeze(['M', 'F', 'O']);

// Retention cohorts
const COHORT_MAX_ROWS = 10;
const COHORT_COLUMNS = Object.freeze({
  [GRAN.WEEK]: [1, 2, 3, 4, 5],
  [GRAN.MONTH]: [1, 7, 15, 30, 90],
});
// Day n retention cards of the Users retention page, the days of the monthly cohort columns
const RETENTION_DAYS = COHORT_COLUMNS[GRAN.MONTH];
// Days of the average retention curve of the Users retention page
const RETENTION_CURVE_DAYS = Object.freeze([0, 1, 3, 7, 14, 21, 30, 60, 90]);

// Windows of the period length: this period and the 5 before it in the Overview new users chart,
// this period and the 7 before it in the comparison charts of the Users growth and Events evolution
const OVERVIEW_WINDOWS = 6;
// Cities shown on each Overview map; the smaller ones are joined in a point per country
const OVERVIEW_MAP_CITIES = 1000;
const COMPARISON_WINDOWS = 8;
// Ranges of the Overview totals modals: their length in days and how many days before today they end
const OVERVIEW_WINDOW_RANGES = Object.freeze([
  {id: 'today', days: 1, back: 0},
  {id: 'yesterday', days: 1, back: 1},
  {id: 'last7', days: 7, back: 0},
  {id: 'last30', days: 30, back: 0},
  {id: 'last90', days: 90, back: 0},
  {id: 'year', days: 365, back: 0},
]);

// Rows of the Users rankings top list
const RANKING_ROWS = 10;

// Empty chart data while a page loads or when the endpoint has none; the arrays stay unfrozen because Chart.js
// marks the arrays it draws
const EMPTY_SERIES = Object.freeze({labels: [], values: []});
const EMPTY_BARS = Object.freeze({labels: [], titles: [], values: []});
// Empty step counts per bucket of the Onboarding conversion chart
const EMPTY_STEP_SERIES = Object.freeze({labels: [], counts: {}});

// Google Analytics Data API: date format of the reports, rows per report, reports per batch call and empty dimension value
const GA_DATE_FORMAT = 'YYYYMMDD';
const GA_ROW_LIMIT = 100000;
const GA_REPORTS_PER_CALL = 5;
// Google Analytics batch calls of one page running at the same time (GA4 allows 10 concurrent requests per property,
// a call of 5 reports counts 5) and the tries of a call refused for that limit, with a wait growing by GA_RETRY_MS
const GA_PARALLEL_CALLS = 2;
const GA_RETRIES = 6;
const GA_RETRY_MS = 500;
const GA_NOT_SET = '(not set)';
// Google Analytics (GA4) property of the Dashboard per Firebase project, used when GA4_PROPERTY is not set; a read that
// fails (no Viewer role for the functions service account) falls back to the other sources (fromGoogle)
const GA4_PROPERTIES = Object.freeze({musicvoxtom: '516108378', 'voxgo-develop': '515662358'});

// Key the nightly rebuild of a Dashboard index sets once the index is filled (the readers fall back until then)
const INDEX_READY_KEY = 'Ready';
// Firestore collection of the user profiles
const USERS_COLLECTION = 'users';
const VOXPOINTS_COLLECTION = 'voxpoints';
// Firebase Auth sign-in providers counted by the Users pages, by page key
const SIGN_IN_PROVIDERS = Object.freeze({google: 'google.com', apple: 'apple.com'});
// Shifted days kept by shift (common/dashboard/daily.js) before the cache is emptied
const SHIFT_CACHE_MAX = 100000;
// meta.createdEnv of the events imported by scripts
const SCRIPT_ENV = 'script';

module.exports = {
  INDEX_READY_KEY,
  GA4_PROPERTIES,
  SIGN_IN_PROVIDERS,
  DAYS_IN_WEEK,
  DAYS_IN_MONTH,
  DAYS_IN_QUARTER,
  HOURS_IN_DAY,
  PERCENT_MAX,
  PER_DAY_MIN_DAYS,
  TREND_DAYS,
  WEEKDAYS,
  DATE_FORMAT,
  MONTH_FORMAT,
  GRAN,
  DAY_GRANULARITY_MAX,
  WEEK_GRANULARITY_MAX,
  PRESET,
  COMPARISON,
  AGE,
  PLATFORM,
  INFORMED_GENDERS,
  COHORT_MAX_ROWS,
  COHORT_COLUMNS,
  RETENTION_DAYS,
  RETENTION_CURVE_DAYS,
  OVERVIEW_WINDOWS,
  OVERVIEW_MAP_CITIES,
  OVERVIEW_WINDOW_RANGES,
  COMPARISON_WINDOWS,
  RANKING_ROWS,
  EMPTY_SERIES,
  EMPTY_BARS,
  EMPTY_STEP_SERIES,
  GA_DATE_FORMAT,
  GA_ROW_LIMIT,
  GA_REPORTS_PER_CALL,
  GA_PARALLEL_CALLS,
  GA_RETRIES,
  GA_RETRY_MS,
  GA_NOT_SET,
  USERS_COLLECTION,
  VOXPOINTS_COLLECTION,
  SHIFT_CACHE_MAX,
  SCRIPT_ENV,
};
